// Key/label rules for the rows of the definition editor that carry a hand-editable key — status dimensions
// and node attributes. Kept in its own module (no React, no api-client) so the rules can be unit-tested
// directly; re-exported from ./shared so every existing import site is unchanged.

export function slug(s: string, fallback: string): string {
  const v = s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return v || fallback;
}

/**
 * KEY-280. The keys this editor session loaded from the server — i.e. the ones a version already saved (and
 * possibly published) may have stamped onto live dials. Instance data is matched to a definition by key on
 * upgrade, so moving one of these is a data event, not a rename: item statuses on a dropped dimension key are
 * hard-deleted, and values on a dropped attribute key are orphaned. Rows added later in the same session
 * aren't in the set — nothing live can be pinned to a key that has never left the browser.
 */
export function lockedKeySet(rows: readonly { key: string }[] | null | undefined): ReadonlySet<string> {
  return new Set((rows ?? []).map((r) => r.key).filter((k) => !!k));
}

/**
 * Whether a row's key should still auto-follow its label as the label is typed (attributes, dimensions).
 * True while the key is empty or still reads as the slug of the label it was derived from — so it tracks
 * "Owner" all the way to `owner`, and stops the moment the key is hand-edited to anything else. Deliberately
 * stateless rather than a "user touched the key" flag: the editor tabs unmount when you switch away from
 * them, so per-row local state would silently reset mid-edit. Clearing the key by hand puts a row back on auto.
 *
 * `lockedKeys` (KEY-280) overrides all of that: a key already saved to the server never auto-follows, however
 * faithfully it still slugs its label. Without this, retyping the label of a measure that has been live since
 * v1 silently re-slugged its key, and the next upgrade read the old key as removed and the new one as added —
 * deleting every status recorded on it. See {@link lockedKeySet}.
 */
export function keyFollowsLabel(key: string, label: string, fallback: string, lockedKeys?: ReadonlySet<string>): boolean {
  if (key && lockedKeys?.has(key)) {
    return false;
  }
  return !key || key === slug(label, fallback);
}

/**
 * The patch to apply when a row's LABEL is edited: the new label, plus the re-slugged key only when the key is
 * still following the label and isn't locked. One function so both editors ask the same question — the bug this
 * replaces was two hand-written copies of the same expression, only one of which anyone thought to check.
 *
 * `key` is absent from the patch (rather than unchanged) when the key must not move, so a caller spreading it
 * over the row can't overwrite a key it didn't mean to touch.
 */
export function labelChangePatch(
  row: { key: string; label: string },
  nextLabel: string,
  fallback: string,
  lockedKeys?: ReadonlySet<string>,
): { label: string; key?: string } {
  return keyFollowsLabel(row.key, row.label, fallback, lockedKeys)
    ? { label: nextLabel, key: slug(nextLabel, fallback) }
    : { label: nextLabel };
}
